import { Box, CircularProgress, Divider, alpha } from '@mui/material';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { ImageOff } from 'mdi-material-ui';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { VigoAttachment } from '../..';
import { AttachmentDto } from 'src/models/attachment';
import dynamic from 'next/dynamic';
import { VigoPdfViewerProps } from 'src/@vigo/vigo-pdfviewer';
import { VigoTypography } from 'src/@vigo/vigo-typography';
import { ViewerToolbar } from 'src/@vigo/vigo-pdfviewer/components/ViewerToolbar';
import { ToolbarButton } from 'src/@vigo/vigo-pdfviewer/components/ToolbarButton';
import RenderTiff from './RenderTiff';

const VigoPdfViewer = dynamic<VigoPdfViewerProps>(
  () => import('src/@vigo/vigo-pdfviewer').then(mod => mod.VigoPdfViewer),
  {
    ssr: false
  }
);

const Centered = ({ children }: { children: ReactNode }) => (
  <Box sx={{ flex: 1, minHeight: '300px', display: 'grid', placeItems: 'center' }}>{children}</Box>
);

const NoPreview = () => (
  <Centered>
    <Box sx={{ display: 'grid', justifyItems: 'center', gap: 2, color: 'text.secondary' }}>
      <ImageOff sx={{ fontSize: '3rem' }} />
      <VigoTypography value="No Preview Available" variant="body2" />
    </Box>
  </Centered>
);

export interface VigoFileViewerProps {
  file: any;
}

export const VigoFileViewer = ({ file }: VigoFileViewerProps) => {
  // null = loading, '' = failed, string = ready
  const [pdfBase64, setPdfBase64] = useState<string | null>(null);
  // rotation, zoom and load failure are file-coupled so they auto-reset when a new file opens
  const [rotState, setRotState] = useState<{ file: unknown; deg: number }>({ file: null, deg: 0 });
  const [scaleState, setScaleState] = useState<{ file: unknown; scale: number }>({ file: null, scale: 1 });
  const [errorState, setErrorState] = useState<unknown>(null);
  const rotation = rotState.file === file ? rotState.deg : 0;
  const scale = scaleState.file === file ? scaleState.scale : 1;
  const imageFailed = errorState === file;

  const mimeType = useMemo(
    () => ((file as VigoAttachment)?.type ?? (file as AttachmentDto)?.fileType ?? '').toLowerCase(),
    [file]
  );

  const isTiff = mimeType === 'image/tiff' || mimeType === 'image/tif';
  const isPdf = mimeType === 'application/pdf' || (file?.name ?? file?.fileName ?? '').toLowerCase().endsWith('.pdf');
  const isImage = mimeType.startsWith('image/');

  const previewUrl = useMemo(() => {
    if (!file || !(file instanceof Blob)) return '';

    return URL.createObjectURL(file);
  }, [file]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  useEffect(() => {
    let active = true;

    const load = async () => {
      if (!file || !isPdf) {
        setPdfBase64(null);

        return;
      }

      setPdfBase64(null);

      const existing = (file as any)?.base64 ?? (file as any)?.content;
      if (typeof existing === 'string' && existing.length > 0) {
        if (active) setPdfBase64(existing.includes(',') ? existing.split(',').pop() ?? '' : existing);

        return;
      }

      if (!(file instanceof Blob)) {
        if (active) setPdfBase64('');

        return;
      }

      try {
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve((reader.result as string) ?? '');
          reader.onerror = reject;
        });
        if (active) setPdfBase64(base64.includes(',') ? base64.split(',').pop() ?? '' : base64);
      } catch {
        if (active) setPdfBase64('');
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [file, isPdf]);

  if (!file) return null;

  if (isTiff) {
    return (
      <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
        <RenderTiff file={file} />
      </Box>
    );
  }

  if (isPdf) {
    if (pdfBase64 === null) {
      return (
        <Centered>
          <CircularProgress />
        </Centered>
      );
    }

    if (pdfBase64 === '') return <NoPreview />;

    return (
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          '& > *': { width: '100% !important', height: '100% !important' },
          overflow: 'auto'
        }}>
        <VigoPdfViewer file={file} fileBase64={pdfBase64} fileName={file.name ?? file.fileName} fullScreen={false} />
      </Box>
    );
  }

  if (isImage && previewUrl && !imageFailed) {
    return (
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: '300px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: theme => (theme.palette.mode === 'dark' ? alpha('#000', 0.35) : theme.palette.grey[100])
        }}>
        <TransformWrapper
          key={previewUrl}
          centerOnInit
          minScale={0.5}
          maxScale={8}
          wheel={{ step: 0.08 }}
          doubleClick={{ mode: 'reset' }}
          onTransformed={(_, state) => setScaleState({ file, scale: state.scale })}>
          {({ zoomIn, zoomOut, resetTransform }) => {
            const rotate = (deg: number) => {
              setRotState({ file, deg: rotation + deg });
              resetTransform();
            };

            return (
              <>
                <ViewerToolbar>
                  <ToolbarButton label="Zoom out" icon={<ZoomOutIcon fontSize="small" />} onClick={() => zoomOut()} />
                  <VigoTypography
                    value={`${Math.round(scale * 100)}%`}
                    variant="body2"
                    align="center"
                    disableTranslation
                    sx={{ minWidth: '46px', fontVariantNumeric: 'tabular-nums' }}
                  />
                  <ToolbarButton label="Zoom in" icon={<ZoomInIcon fontSize="small" />} onClick={() => zoomIn()} />
                  <Divider orientation="vertical" flexItem />
                  <ToolbarButton
                    label="Rotate left"
                    icon={<RotateLeftIcon fontSize="small" />}
                    onClick={() => rotate(-90)}
                  />
                  <ToolbarButton
                    label="Rotate right"
                    icon={<RotateRightIcon fontSize="small" />}
                    onClick={() => rotate(90)}
                  />
                  <Divider orientation="vertical" flexItem />
                  <ToolbarButton
                    label="Fit to screen"
                    icon={<FitScreenIcon fontSize="small" />}
                    onClick={() => resetTransform()}
                  />
                </ViewerToolbar>

                <TransformComponent
                  wrapperStyle={{ flex: 1, minHeight: 0, width: '100%' }}
                  contentStyle={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                  <img
                    src={previewUrl}
                    alt={file.name ?? file.fileName}
                    onError={() => setErrorState(file)}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      transform: `rotate(${rotation}deg)`,
                      transition: 'transform 0.2s ease'
                    }}
                  />
                </TransformComponent>
              </>
            );
          }}
        </TransformWrapper>
      </Box>
    );
  }

  return <NoPreview />;
};
